import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Chip
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import DeleteIcon from '@mui/icons-material/Delete';
import MicIcon from '@mui/icons-material/Mic';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import TextSnippetIcon from '@mui/icons-material/TextSnippet';
import { TranscriptionRecord, transcriptionStorage } from '../utils/transcriptionStorage';
import { getTranslations, getUILanguage } from '../utils/translations';

interface TranscriptionHistoryProps {
  onTranscriptionSelect?: (transcription: TranscriptionRecord) => void;
  language?: string;
}

const TranscriptionHistory: React.FC<TranscriptionHistoryProps> = ({
  onTranscriptionSelect,
  language = 'en-US'
}) => {
  // Get UI language and translations
  const uiLanguage = getUILanguage(language);
  const t = getTranslations(uiLanguage);
  const [transcriptions, setTranscriptions] = useState<TranscriptionRecord[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [audioElements, setAudioElements] = useState<Record<string, HTMLAudioElement>>({});

  useEffect(() => {
    loadTranscriptions();
  }, []);

  const loadTranscriptions = async () => {
    try {
      const stored = await transcriptionStorage.getAllTranscriptions();
      setTranscriptions(stored);
    } catch (error) {
      setTranscriptions([]);
    }
  };

  const handlePlay = async (transcription: TranscriptionRecord) => {
    if (playingId === transcription.id) {
      // Pause
      const audio = audioElements[transcription.id];
      if (audio) {
        audio.pause();
        setPlayingId(null);
      }
      return;
    }

    // Stop any currently playing audio
    if (playingId && audioElements[playingId]) {
      audioElements[playingId].pause();
    }

    try {
      // Get fresh audio URL from IndexedDB
      let audioUrl = transcription.audioUrl;
      if (!audioUrl) {
        audioUrl = await transcriptionStorage.getAudioUrl(transcription.id);
        if (!audioUrl) {
          return;
        }
      }

      // Create or get audio element
      let audio = audioElements[transcription.id];
      if (!audio || audio.src !== audioUrl) {
        audio = new Audio(audioUrl);
        audio.onended = () => setPlayingId(null);
        audio.onerror = (e) => {
          setPlayingId(null);
        };
        setAudioElements(prev => ({ ...prev, [transcription.id]: audio }));
      }

      await audio.play();
      setPlayingId(transcription.id);
    } catch (error) {
      setPlayingId(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await transcriptionStorage.deleteTranscription(id);
      loadTranscriptions();
      
      // Clean up audio element
      if (audioElements[id]) {
        audioElements[id].pause();
        delete audioElements[id];
        if (playingId === id) {
          setPlayingId(null);
        }
      }
    } catch (error) {
    }
  };

  const handleDownloadAudio = async (transcription: TranscriptionRecord) => {
    try {
      // Get fresh audio URL if needed
      let audioUrl = transcription.audioUrl;
      if (!audioUrl) {
        audioUrl = await transcriptionStorage.getAudioUrl(transcription.id);
        if (!audioUrl) {
          return;
        }
      }
      
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `voxist_audio_${transcription.timestamp.toISOString().slice(0, 19).replace(/[:.]/g, '-')}.wav`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
    }
  };

  const handleDownloadText = (transcription: TranscriptionRecord) => {
    const content = `Transcription\n=============\n\nTimestamp: ${transcription.timestamp.toLocaleString()}\nLanguage: ${transcription.language}\nType: ${transcription.isRealTime ? 'Live Recording' : 'File Upload'}\n\nText:\n${transcription.text}`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = `voxist_transcription_${transcription.timestamp.toISOString().slice(0, 19).replace(/[:.]/g, '-')}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const formatDate = (date: Date) => {
    // Map UI languages to appropriate locales for date formatting
    const localeMap: Record<string, string> = {
      'en-US': 'en-US',
      'fr-FR': 'fr-FR',
      'de-DE': 'de-DE',
      'es-ES': 'es-ES',
      'it-IT': 'it-IT',
      'nl-NL': 'nl-NL',
      'sv-SE': 'sv-SE',
      'pt-PT': 'pt-PT'
    };
    
    const locale = localeMap[uiLanguage] || 'en-US';
    
    return new Intl.DateTimeFormat(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: locale === 'en-US' // Use 12-hour format only for English
    }).format(date);
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const getLanguageFlag = (language: string) => {
    const flags: Record<string, string> = {
      'fr-FR': '🇫🇷',
      'fr-medical': '🇫🇷🏥',
      'en-US': '🇺🇸',
      'de-DE': '🇩🇪',
      'nl-NL': '🇳🇱',
      'it-IT': '🇮🇹',
      'es-ES': '🇪🇸',
      'sv-SE': '🇸🇪',
      'pt-PT': '🇵🇹'
    };
    return flags[language] || '🌐';
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Typography 
        variant="h6" 
        sx={{ 
          p: 2, 
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.9)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}
      >
{t.previousTranscriptions}
      </Typography>
      
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {transcriptions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="rgba(255, 255, 255, 0.6)">
{t.noTranscriptions}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {transcriptions.map((transcription) => (
              <ListItem
                key={transcription.id}
                sx={{
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.02)'
                  },
                  cursor: 'pointer',
                  py: 1.5
                }}
                onClick={() => onTranscriptionSelect?.(transcription)}
              >
                <Box sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography variant="caption" color="rgba(255, 255, 255, 0.7)">
                      {formatDate(transcription.timestamp)}
                    </Typography>
                    <Chip
                      size="small"
                      label={getLanguageFlag(transcription.language)}
                      sx={{ 
                        minWidth: 'auto', 
                        height: 20,
                        fontSize: '0.7rem',
                        '& .MuiChip-label': { px: 0.5 }
                      }}
                    />
                    <Chip
                      size="small"
                      icon={transcription.isRealTime ? <MicIcon /> : <FileUploadIcon />}
                      label={transcription.isRealTime ? t.live : t.file}
                      sx={{ 
                        height: 20,
                        fontSize: '0.7rem',
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        color: 'rgba(255, 255, 255, 0.8)',
                        '& .MuiChip-icon': { 
                          fontSize: '0.8rem',
                          color: 'rgba(255, 255, 255, 0.8)'
                        }
                      }}
                    />
                  </Box>
                  
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255, 255, 255, 0.9)',
                      lineHeight: 1.4,
                      mb: 1
                    }}
                  >
                    {truncateText(transcription.text)}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    {(transcription.audioUrl || (transcription as any).audioBlob) && (
                      <Tooltip title={playingId === transcription.id ? t.pause : t.playAudio}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlay(transcription);
                          }}
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          {playingId === transcription.id ? <PauseIcon /> : <PlayArrowIcon />}
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {(transcription.audioUrl || (transcription as any).audioBlob) && (
                      <Tooltip title={t.downloadAudio}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadAudio(transcription);
                          }}
                          sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title={t.downloadTranscription}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadText(transcription);
                        }}
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        <TextSnippetIcon />
                      </IconButton>
                    </Tooltip>
                    
                    <Tooltip title={t.delete}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(transcription.id);
                        }}
                        sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default TranscriptionHistory;